import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";

// Form
import FormInput from "./FormInput";
import countries from "./Helpers/JSON/Countries.json";

// Functions
import { AltArrowDown } from "@solar-icons/react";
import { useTranslation } from "react-i18next";
import CheckSimilarity from "./Helpers/Functions/CheckSimilarity";
import SortByKey from "./Helpers/Functions/SortByKey";

// ─── Types ───────────────────────────────────────────────────────────────────

/** Shape of a single country entry from Countries.json */
type CountryEntry = (typeof countries)[number];

export interface CountryListInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  /** RHF control object from useForm */
  control: Control<TFieldValues>;
  /** Field name registered in the form */
  name: TName;
  /** Validation rules (e.g. { required: "Required" }) */
  rules?: RegisterOptions<TFieldValues, TName>;
  /** Input label text */
  label: string;
  /** Whether the input is read-only */
  readOnly?: boolean;
  /** Show country code (e.g. "AE") instead of name in the dropdown */
  showCountryCode?: boolean;
  /** Optional callback when country changes (receives country code) */
  onCountryChange?: (code: string) => void;

  required?: boolean;
  forPhoneNumber?: boolean;

  floating?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Find a country by localized name (case-insensitive).
 * Returns the matched CountryEntry or undefined.
 */
const findCountryByName = (
  input: string,
  lng: string,
): CountryEntry | undefined => {
  if (!input) return undefined;
  const normalized = input.trim().toLowerCase();

  return countries.find(
    (c) =>
      c.name[lng]?.toLowerCase() === normalized ||
      c.name["en"]?.toLowerCase() === normalized,
  );
};

/**
 * Find a country by code (e.g. "PS", "AE").
 */
const findCountryByCode = (code: string): CountryEntry | undefined => {
  if (!code) return undefined;
  return countries.find((c) => c.code === code);
};

const CountryFlag = ({ code, alt }: { code: string; alt: string }) => {
  return (
    <img
      src={`https://flagcdn.com/24x18/${code?.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/48x36/${code?.toLowerCase()}.png 2x,
    https://flagcdn.com/72x54/${code?.toLowerCase()}.png 3x`}
      width="24"
      height="18"
      alt={alt}
    />
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CountryListInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  required,
  label,
  floating = true,
  readOnly = false,
  showCountryCode = false,
  onCountryChange,
  forPhoneNumber,
}: CountryListInputProps<TFieldValues, TName>) {
  const { i18n } = useTranslation();
  const currentLng = i18n.language || "en";
  const direction = i18n.dir();

  // ── RHF controller ──
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules });

  // The form value is a country code (e.g. "PS")
  const selectedCode: string = field.value ?? "";
  const selectedCountry = useMemo(
    () => findCountryByCode(selectedCode),
    [selectedCode],
  );

  // ── Local search text (what the user types while dropdown is open) ──
  const [searchText, setSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Derived display text from the form value (used when not editing)
  const displayText = useMemo(() => {
    if (!selectedCountry) return "";
    const countryName =
      selectedCountry.name[currentLng] || selectedCountry.name["en"];
    if (!forPhoneNumber) {
      return countryName;
    }

    return (
      "(+" +
      selectedCountry.dial_code +
      ") " +
      (selectedCountry.name[currentLng] || selectedCountry.name["en"])
    );
  }, [selectedCountry, currentLng, forPhoneNumber]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  // ── Handlers ──

  const handleFocus = useCallback(() => {
    if (readOnly) return;
    setSearchText(displayText);
    setIsOpen(true);
  }, [readOnly, displayText]);

  const handleBlur = useCallback(() => {
    // Delay so click events on the list can fire first
    setTimeout(() => {
      // If the dropdown was already closed by handleCountrySelect,
      // skip the match logic — a selection was already made.
      setIsOpen((wasOpen) => {
        if (wasOpen) {
          // Blur without selection: try to match what the user typed
          const match = findCountryByName(searchText, currentLng);
          if (match) {
            field.onChange(match.code);
            onCountryChange?.(match.code);
          } else {
            if (!selectedCountry) {
              field.onChange("");
              onCountryChange?.("");
              setSearchText("");
            }
          }
        }
        return false;
      });
      field.onBlur();
    }, 200);
  }, [searchText, currentLng, selectedCountry, field, onCountryChange]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    // Don't update form value while typing — only on blur or selection
  }, []);

  const handleCountrySelect = useCallback(
    (code: string) => {
      field.onChange(code);
      onCountryChange?.(code);
      setSearchText("");
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [field, onCountryChange],
  );

  // We pass an empty register object because FormInput expects it,
  // but useController handles the actual registration.
  // Pass field.ref through the register object so FormInput
  // can attach it to the <input> element via registerRef.
  const fieldRegister = useMemo(
    () => ({
      ref: (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof field.ref === "function") {
          field.ref(el);
        } else if (field.ref) {
          (field.ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }
      },
    }),
    [field.ref],
  );

  return (
    <FormInput
      // @ts-expect-error qwe
      register={fieldRegister}
      name={name}
      floating={floating}
      id={name}
      required={required}
      appendIcon={<AltArrowDown />}
      label={label}
      error={error}
      prependIcon={
        selectedCountry?.code ? (
          <div className="px-3 fs-4">
            <CountryFlag
              code={selectedCountry?.code}
              alt={
                selectedCountry?.name[currentLng] || selectedCountry?.name["en"]
              }
            />
          </div>
        ) : null
      }
      value={isOpen ? searchText : displayText}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      readOnly={readOnly}
      dir={direction}
    >
      <CountryList
        isOpen={isOpen}
        searchText={isOpen ? searchText : ""}
        onSelect={handleCountrySelect}
        showCountryCode={showCountryCode}
        direction={direction}
      />
    </FormInput>
  );
}

// ─── CountryList Sub-Component ───────────────────────────────────────────────

type ListStyle = { opacity: string; display: string };

const COUNTRY_LIST_STYLES: Record<"HIDE" | "FADE" | "SHOW", ListStyle> = {
  HIDE: { opacity: "1", display: "none" },
  FADE: { opacity: "0", display: "block" },
  SHOW: { opacity: "1", display: "block" },
};

interface CountryListProps {
  /** Called when a country is clicked (passes country code) */
  onSelect: (code: string) => void;
  /** Current search text to filter by */
  searchText: string;
  /** Whether the list is open */
  isOpen: boolean;
  /** Show country code instead of name */
  showCountryCode: boolean;
  /** Text direction */
  direction?: string;
  required?: boolean;
  forPhoneNumber?: boolean;
}

const CountryList: React.FC<CountryListProps> = React.memo(
  function CountryList({
    onSelect,
    forPhoneNumber = false,
    searchText,
    isOpen,
    showCountryCode,
    direction = "ltr",
  }) {
    const { i18n } = useTranslation();
    const currentLng = i18n.language || "en";

    // Localize country names once per language change
    const countriesWithLocalizedNames = useMemo(
      () =>
        countries.map((c) => ({
          ...c,
          localizedName: c.name[currentLng] || c.name["en"],
        })),
      [currentLng],
    );

    // Filter & sort by similarity
    const sortedList = useMemo(() => {
      if (!searchText) {
        return SortByKey([...countriesWithLocalizedNames], "localizedName");
      }

      const query = searchText.toLowerCase();
      const filtered = countriesWithLocalizedNames
        .filter((c) =>
          [c.code, c.name["ar"], c.name["en"]].some((name) =>
            name.toLowerCase().includes(query),
          ),
        )
        .map((c) => ({
          ...c,
          similarity: Math.max(
            CheckSimilarity(c.name["en"], searchText),
            CheckSimilarity(c.name["ar"], searchText),
            CheckSimilarity(c.code?.toLowerCase(), searchText),
          ),
        }));

      if (filtered.length > 0) {
        return SortByKey(filtered, "similarity").reverse();
      }

      // Fall back to showing all countries if nothing matches
      return SortByKey([...countriesWithLocalizedNames], "localizedName");
    }, [searchText, countriesWithLocalizedNames]);

    // ── Visibility animation ──
    const [styles, setStyles] = useState(COUNTRY_LIST_STYLES.HIDE);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (isOpen) {
        setStyles(COUNTRY_LIST_STYLES.SHOW);
      } else {
        setStyles(COUNTRY_LIST_STYLES.FADE);
        timeoutRef.current = setTimeout(() => {
          setStyles(COUNTRY_LIST_STYLES.HIDE);
        }, 150);
      }

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, [isOpen]);

    if (styles.display === "none") return null;

    return (
      <div
        className="country-list"
        style={styles}
        dir={direction}
        role="listbox"
      >
        {sortedList.map((item, index) => (
          <div
            key={`${item.code}-${index}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(item.code)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onSelect(item.code);
              }
            }}
            className="country-item px-2 d-flex align-items-center"
            role="option"
            aria-selected={false}
            tabIndex={0}
          >
            <div className="mx-2" style={{ fontSize: "x-large" }}>
              <CountryFlag code={item.code} alt={item.localizedName} />
            </div>
            <div>
              {forPhoneNumber ? `(+${item.dial_code}) ` : null}
              {showCountryCode ? item.code : item.localizedName}
            </div>
          </div>
        ))}
      </div>
    );
  },
);
